/**
 * Operation Error Dialog
 *
 * Thin wrapper around the shared ErrorDialog for failed operations.
 * For version_uploaded operations, looks up the version's structured
 * error_details. For other operations, transforms events into error rows.
 */

import { useMemo } from 'react';
import type { TableOperation } from '@loader/shared-types';
import {
  TABLE_OPERATION_TYPE_LABELS,
  formatOperationSummary,
} from '@loader/shared-types';

import { useOperationEventsQuery, useTableVersionsQuery } from '@/features/tables/hooks/useTableQueries';
import { formatDateTimeShort, getOperationDuration } from '@/lib/formatters';
import { ErrorDialog, parseErrorToRows, operationEventsToErrorRows, formatErrorCount } from '@/components/common';

interface OperationLogsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tableId: string;
  operation: TableOperation | null;
}

export function OperationLogsDialog({
  open,
  onOpenChange,
  tableId,
  operation,
}: OperationLogsDialogProps) {
  const isVersionUpload = operation?.operation_type === 'version_uploaded';

  const { data: eventsResponse } = useOperationEventsQuery(tableId, operation?.id, {
    enabled: open && !!operation?.id && !isVersionUpload,
    limit: 100,
  });

  const { data: versions } = useTableVersionsQuery(tableId, {
    enabled: open && isVersionUpload && !!operation?.version_id,
  });

  const events = eventsResponse?.events;
  const durationStr = operation ? getOperationDuration(operation.started_at, operation.completed_at) : '-';
  const summary = operation ? formatOperationSummary(operation.operation_type, operation.params) : '';

  const { errorRows, isTruncated } = useMemo(() => {
    // Version upload errors: use version's structured error_details
    if (isVersionUpload && operation?.version_id && versions) {
      const version = versions.find((v) => v.id === operation.version_id);
      if (version?.error_message) {
        const { rows, isTruncated } = parseErrorToRows(version.error_message, version.error_details);
        return { errorRows: rows, isTruncated };
      }
    }

    // Operation errors with structured error_details in result (type_conversion, constraint_change)
    const resultErrorDetails = operation?.result?.error_details;
    if (resultErrorDetails && typeof resultErrorDetails === 'object' && 'category' in resultErrorDetails) {
      const { rows, isTruncated } = parseErrorToRows(operation.error_message ?? '', resultErrorDetails);
      return { errorRows: rows, isTruncated };
    }

    // Fallback: operation events
    if (events && events.length > 0) {
      return { errorRows: operationEventsToErrorRows(events), isTruncated: false };
    }

    if (operation?.error_message) {
      return { errorRows: [{ field: '-', type: 'Error', issue: operation.error_message }], isTruncated: false };
    }

    return { errorRows: [], isTruncated: false };
  }, [isVersionUpload, operation, versions, events]);

  return (
    <ErrorDialog
      open={open}
      onOpenChange={onOpenChange}
      title={operation ? TABLE_OPERATION_TYPE_LABELS[operation.operation_type] : 'Operation Failed'}
      description={
        <span className="flex flex-col gap-1 text-sm">
          {summary && <span>{summary}</span>}
          <span className="flex items-center gap-4 text-xs">
            {operation?.started_at && (
              <span>Started: {formatDateTimeShort(operation.started_at)}</span>
            )}
            {durationStr !== '-' && <span>Duration: {durationStr}</span>}
          </span>
        </span>
      }
      rows={errorRows}
      isTruncated={isTruncated}
      exportFileName={`errors-${operation?.operation_type ?? 'operation'}.xlsx`}
      footerMessage={errorRows.length > 0
        ? `${formatErrorCount(errorRows.length, isTruncated)} found. Fix the issues and retry.`
        : undefined
      }
    />
  );
}

